import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import AuthLayout from './AuthLayout';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/$/, '');

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    password_confirmation: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setLocalError('Invalid reset link. Please request a new password reset.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (localError) setLocalError('');
    if (successMessage) setSuccessMessage('');
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      return 'Password must contain uppercase, lowercase, numbers, and special characters.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setLocalError('');
    setSuccessMessage('');

    if (!token) {
      setLocalError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!formData.password || formData.password.trim() === '') {
      setLocalError('Password is required.');
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      setLocalError('Passwords do not match.');
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setLocalError(passwordError);
      return;
    }

    setShowLoading(true);

    try {
      const response = await fetch(`${API_BASE}/users/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setLocalError(data.message || `Reset failed (HTTP ${response.status})`);
        return;
      }

      if (!data.success) {
        setLocalError(data.message || 'Password reset failed');
        return;
      }

      setSuccessMessage(data.message || 'Password has been reset successfully.');

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      setLocalError('An error occurred. Please try again.');
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Set New Password"
      subtitle="Please enter a strong password containing letters, numbers, and special characters."
    >
      <form onSubmit={handleSubmit} className="lp-auth-form" noValidate>
        <div className="lp-form-group">
          <label htmlFor="rp-password" className="lp-label">
            New Password <span className="lp-required">*</span>
          </label>
          <div className="lp-input-container">
            <input
              id="rp-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your new password"
              className="lp-input lp-password-input"
              required
              autoComplete="new-password"
              autoFocus
            />
            <button
              type="button"
              className="lp-eye-btn"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>
        </div>

        <div className="lp-form-group">
          <label htmlFor="rp-confirm" className="lp-label">
            Confirm Password <span className="lp-required">*</span>
          </label>
          <div className="lp-input-container">
            <input
              id="rp-confirm"
              type={showPasswordConfirmation ? 'text' : 'password'}
              name="password_confirmation"
              value={formData.password_confirmation}
              onChange={handleChange}
              placeholder="Re-enter your new password"
              className="lp-input lp-password-input"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="lp-eye-btn"
              onClick={() => setShowPasswordConfirmation((v) => !v)}
              aria-label={showPasswordConfirmation ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPasswordConfirmation ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>
        </div>

        {localError && <p className="lp-form-error">{localError}</p>}
        {successMessage && <p className="lp-form-success">✓ {successMessage}</p>}

        <button
          type="submit"
          className="lp-btn-signin"
          disabled={showLoading}
        >
          {showLoading && <span className="lp-btn-spinner" />}
          <span>{showLoading ? 'Resetting Password...' : 'Reset Password'}</span>
        </button>

        <div className="lp-back-wrap">
          <a
            href="/login"
            className="lp-forgot-pass-link"
            onClick={(e) => {
              e.preventDefault();
              navigate('/login');
            }}
          >
            ← Back to Sign In
          </a>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
